import type { EnemyKind } from '@somnhold/content';
import { imax, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Hero, HookRules, HookState, SendaNpc } from '../types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';

/**
 * The hooks (docs/sendas/guerrero.md, chapter 2, act 1: «Los garfios»). On this side's edge Kel winds the two old
 * winches to raise the span (two minutes of crank, the bar in sight; a blow stops him a moment). From the Furrows the
 * nightmares come up throwing hooks: a bone hook on a rope bites the edge with a rattle of chain `warn` before the
 * first one shows, and the rope shakes as many times as what climbs it: once for one, twice for a band, three times
 * for the shadow ogre on the thickest rope. Standing by a rope a moment cuts it, and whatever is still on it falls
 * back to the Furrows; what has come over stays. Gargoyles need no rope. You can't cut them all: read the ropes and
 * choose. Won with the span up; lost with the hero or Kel down.
 *
 * Cost: the plan is worked out by the server's builder (campaign/sendas.ts); here a handful of ropes are looked at once
 * a tick, each against the one hero.
 */

const fresh = (): HookState => ({ ropes: [], next: 0, wound: 0, at: -1 });

/** Kel winding: the path's person at the winch (the act's first). */
const kelOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'kel');

/** The hero sent to a rope's anchor (a tap on it): it walks there, and standing by it cuts it. */
export const hookOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.hooks;
  const w = s.senda?.hooks;
  if (!r || !w || at < 0 || at >= r.anchors.length) return;
  w.at = at;
  const a = r.anchors[at] as { x: number; y: number };
  // A step in from the edge, on the land's side.
  orderMove(s, h, a.x - r.out.x * 3, a.y - r.out.y * 3);
  h.target = 0;
  h.forced = false;
};

/** A climber over the edge at its anchor (a little out on the drop's side, stepping onto the land). */
const climb = (s: BattleState, r: HookRules, kind: string, at: { x: number; y: number }, big: boolean): void => {
  const k = kind as EnemyKind;
  const e = spawnEnemy(s, k, 1, false, 0, at.x + r.out.x, at.y + r.out.y, big ? r.ogreHp : 0);
  if (!big && s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  e.anim = 'walk';
  ev(s, { t: 'senda', kind: 'climb', npc: kind, x: e.x, y: e.y, n: big ? 3 : 1 });
};

export const hooksTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.hooks;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.hooks ??= fresh());
  if (!s.started) return;
  // The winch: Kel winds while he stands and nothing just struck him.
  const kel = kelOf(s);
  if (kel && kel.hp > 0) {
    if (kel.flinch > 0) kel.flinch--;
    else if (w.wound < r.winch) {
      if (w.wound === 0) cue('winch');
      w.wound++;
    }
  }
  // The ropes that bite now.
  while (w.next < r.plan.length && (r.plan[w.next] as HookRules['plan'][number]).t <= s.waveT) {
    const p = r.plan[w.next] as HookRules['plan'][number];
    w.ropes.push({ i: w.next, cut: false, out: 0, by: 0 });
    const a = r.anchors[p.at] as { x: number; y: number };
    ev(s, { t: 'senda', kind: 'hook', npc: '', x: a.x, y: a.y, n: p.shakes });
    if (p.shakes >= 3) cue('ogre');
    w.next++;
  }
  const hero = s.heroes.find((h) => h.downTicks === 0);
  for (const rope of w.ropes) {
    const p = r.plan[rope.i] as HookRules['plan'][number];
    if (rope.cut || rope.out >= p.kinds.length) continue;
    const a = r.anchors[p.at] as { x: number; y: number };
    // Cut: the hero standing by it a moment.
    if (hero && withinRange(hero.x, hero.y, a.x, a.y, r.reach)) {
      if (++rope.by >= r.cut) {
        rope.cut = true;
        ev(s, { t: 'senda', kind: 'cut', npc: '', x: a.x, y: a.y, n: p.kinds.length - rope.out });
        if (w.at === p.at) w.at = -1;
        continue;
      }
    } else rope.by = 0;
    // Over the edge, one by one, from `warn` after the bite.
    const since = s.waveT - p.t - p.warn;
    if (since >= 0 && since >= rope.out * r.climb) {
      climb(s, r, p.kinds[rope.out] as string, a, p.shakes >= 3);
      rope.out++;
    }
  }
  // Ropes done (cut, or everything over) leave the list once the drawing has had a few seconds of them.
  if (w.ropes.length > 12) w.ropes = w.ropes.filter((x) => !(x.cut || x.out >= (r.plan[x.i] as HookRules['plan'][number]).kinds.length) || s.waveT - (r.plan[x.i] as HookRules['plan'][number]).t < r.warn * 4);
  // The order given is done when the hero is by the anchor and nothing more is on its rope.
  if (w.at >= 0 && hero && !hero.moving) {
    const a = r.anchors[w.at] as { x: number; y: number };
    const live = w.ropes.some((x) => {
      const p = r.plan[x.i] as HookRules['plan'][number];
      return p.at === w.at && !x.cut && x.out < p.kinds.length;
    });
    if (!live && withinRange(hero.x, hero.y, a.x, a.y, r.reach)) w.at = -1;
  }
};

/** Whether a rope is still live (bitten, not cut, something still on it): the hook marks and the bot. */
export const ropeLive = (r: HookRules, x: HookState['ropes'][number]): boolean => !x.cut && x.out < (r.plan[x.i] as HookRules['plan'][number]).kinds.length;

/** How far the span is up, of the whole. */
export const hooksGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.hooks;
  return { done: s.senda?.hooks?.wound ?? 0, need: r?.winch ?? 1 };
};

/** Won with the span up. */
export const hooksResult = (s: BattleState): 'won' | null => {
  const r = s.rules?.senda?.hooks;
  const w = s.senda?.hooks;
  return r && w && w.wound >= r.winch ? 'won' : null;
};
